import type { CSSProperties, ReactNode } from 'react';
import { Toaster as SonnerToaster } from 'sonner';

import { useTheme } from '@/app/providers/theme';

/**
 * The toast surface, wired to this application's own theme.
 *
 * Written here rather than taken from the registry because the registry's version reads the theme
 * from `next-themes`, which this application does not use and does not need: `ThemeProvider`
 * already resolves `ui.default_theme` and the system preference into one value, and a second
 * source for the same fact is the one that ends up disagreeing.
 *
 * `richColors` is off. The three semantic colours in this product mean refused, incomplete and
 * accepted at a gate, and a toast library tinting its own success and error states would be a
 * fourth vocabulary for the same three ideas.
 *
 * **Its palette is this product's, through the four variables the library documents for it.**
 * Handing it `theme` alone only chooses between the library's OWN light and dark greys, which are
 * not these greys: a toast drawn in them is the one panel on screen that belongs to somebody else,
 * and the mismatch is worst in the dark theme where its surface is nearly black and every other
 * raised surface here is not. The values name the raw tokens rather than the `--color-*` aliases
 * that stand for them, because this is a style handed to a third party rather than a rule inside
 * this design system: one hop, and nothing to re-check if the alias layer is ever rearranged.
 */
export function Toaster(): ReactNode {
  const { resolved } = useTheme();
  return (
    <SonnerToaster
      theme={resolved}
      position="bottom-right"
      closeButton={false}
      style={
        {
          '--normal-bg': 'var(--surface)',
          '--normal-text': 'var(--surface-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as CSSProperties
      }
    />
  );
}
